/**
 * RechercheDomaine — PEFEP TECH — v5
 * ---------------------------------------------------------------
 * Recherche de domaine et dossier de commande en trois etapes.
 *
 * Ecrit volontairement a plat : pas de fragments, pas d'expressions
 * imbriquees profondement. Les blocs sont extraits en fonctions
 * separees pour rester lisibles et faciles a modifier.
 *
 * NOUVEAU EN v5 — pre-remplissage automatique
 * Le composant accepte une propriete "valeurInitiale". Quand elle
 * est fournie (par le wizard, apres generation du site), le nom de
 * l'entreprise est converti en nom de domaine plausible et la
 * recherche se lance seule. Le client voit la disponibilite de son
 * domaine sans avoir rien tape.
 *
 * Les champs collectes correspondent au profil WHOIS exige par
 * l'ICANN pour tout enregistrement de domaine.
 *
 * NOTE : appelAPI n'ajoute pas de Content-Type (pour laisser passer
 * les envois FormData). Chaque appel JSON doit donc le declarer.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { appelAPI } from '../api';
import './RechercheDomaine.css';

const DEPARTEMENTS = [
  'Ouest',
  'Artibonite',
  'Nord',
  'Nord-Est',
  'Nord-Ouest',
  'Centre',
  'Sud',
  'Sud-Est',
  "Grand'Anse",
  'Nippes',
];

const PAIEMENTS = [
  { cle: 'MonCash', icone: 'M', detail: '+509 4737 6200' },
  { cle: 'NatCash', icone: 'N', detail: '+509 4032 5735' },
  { cle: 'Sogebank', icone: 'S', detail: 'Virement bancaire' },
];

const DOSSIER_VIDE = {
  typeTitulaire: 'particulier',
  organisation: '',
  prenom: '',
  nom: '',
  telephone: '',
  adresse: '',
  ville: '',
  departement: 'Ouest',
  codePostal: '',
  pays: 'HT',
};

/**
 * Transforme un nom d'entreprise en nom de domaine plausible.
 * "Restaurant Chez Marie" devient "restaurantchezmarie".
 */
function nomVersDomaine(texteBrut) {
  return String(texteBrut || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 40);
}

function RechercheDomaine(proprietes) {
  const titre = proprietes.titre || 'Trouvez votre nom de domaine';
  const sousTitre =
    proprietes.sousTitre ||
    'Verifiez la disponibilite et le tarif reel de votre domaine.';

  // Nom pre-rempli, transmis par le wizard apres generation du site.
  const valeurInitiale = proprietes.valeurInitiale || '';

  const { user } = useAuth();
  const navigate = useNavigate();

  const [saisie, setSaisie] = useState('');
  const [chargement, setChargement] = useState(false);
  const [resultats, setResultats] = useState(null);
  const [tauxChange, setTauxChange] = useState(135);
  const [erreur, setErreur] = useState('');

  const [domaineChoisi, setDomaineChoisi] = useState(null);
  const [etape, setEtape] = useState(1);
  const [dossier, setDossier] = useState(DOSSIER_VIDE);
  const [moyenPaiement, setMoyenPaiement] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  function majDossier(champ, valeur) {
    setDossier(function (precedent) {
      const copie = Object.assign({}, precedent);
      copie[champ] = valeur;
      return copie;
    });
  }

  /* ═══════════════ RECHERCHE ═══════════════ */

  async function lancerRecherche(evenement, valeurForcee) {
    if (evenement) evenement.preventDefault();

    // valeurForcee permet de lancer une recherche sans attendre que
    // setSaisie ait mis a jour l'etat : indispensable au declenchement
    // automatique depuis le wizard.
    const terme = String(valeurForcee || saisie).trim();
    if (!terme || chargement) return;

    setChargement(true);
    setErreur('');
    setResultats(null);
    setConfirmation(null);

    try {
      const reponse = await appelAPI('/api/domains/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: terme }),
      });

      const data = reponse.data || {};

      if (!reponse.ok || !data.succes) {
        setErreur(data.message || 'Erreur ' + reponse.statut);
        return;
      }

      setResultats(data);
      if (data.tauxChange) setTauxChange(data.tauxChange);
    } catch (err) {
      setErreur('Connexion impossible. Verifiez votre reseau et reessayez.');
    } finally {
      setChargement(false);
    }
  }

  // Pre-remplissage et recherche automatique depuis le wizard.
  useEffect(
    function () {
      if (!valeurInitiale || !user) return;
      const propose = nomVersDomaine(valeurInitiale);
      if (propose.length < 2) return;
      setSaisie(propose);
      lancerRecherche(null, propose);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valeurInitiale, user]
  );

  /* ═══════════════ DOSSIER ═══════════════ */

  function ouvrirDossier(ligne) {
    setDomaineChoisi(ligne);
    setEtape(1);
    setErreur('');
    setMoyenPaiement('');

    const morceaux = String((user && user.nom) || '').trim().split(' ');
    setDossier(
      Object.assign({}, DOSSIER_VIDE, {
        prenom: morceaux[0] || '',
        nom: morceaux.slice(1).join(' ') || '',
      })
    );
  }

  function fermerDossier() {
    setDomaineChoisi(null);
    setEtape(1);
    setErreur('');
  }

  function reculer() {
    if (etape > 1) {
      setEtape(etape - 1);
    } else {
      fermerDossier();
    }
  }

  function validerEtape() {
    setErreur('');

    if (etape === 1) {
      if (!dossier.prenom.trim() || !dossier.nom.trim()) {
        setErreur('Le prenom et le nom sont obligatoires.');
        return;
      }
      if (dossier.typeTitulaire === 'organisation' && !dossier.organisation.trim()) {
        setErreur("Le nom de l'organisation est obligatoire.");
        return;
      }
      if (!/^\+?[0-9\s-]{8,20}$/.test(dossier.telephone.trim())) {
        setErreur('Numero invalide. Format attendu : +509 0000 0000');
        return;
      }
      setEtape(2);
      return;
    }

    if (etape === 2) {
      if (!dossier.adresse.trim() || !dossier.ville.trim()) {
        setErreur("L'adresse et la ville sont obligatoires.");
        return;
      }
      setEtape(3);
    }
  }

  async function envoyerDossier() {
    if (!moyenPaiement) {
      setErreur('Choisissez un moyen de paiement.');
      return;
    }

    setEnvoi(true);
    setErreur('');

    const corps = Object.assign({}, dossier, {
      domaine: domaineChoisi.domaine,
      prix: domaineChoisi.prix,
      prixRenouvellement: domaineChoisi.prixRenouvellement,
      email: user && user.email,
      moyenPaiement: moyenPaiement,
    });

    try {
      const reponse = await appelAPI('/api/domains/purchase-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });

      const data = reponse.data || {};

      if (!reponse.ok || !data.succes) {
        setErreur(data.message || 'Erreur ' + reponse.statut);
        return;
      }

      setConfirmation({
        id: data.demande.id,
        domaine: data.demande.domaine,
        message: data.message,
        lienWhatsApp: data.lienWhatsApp,
      });

      setDomaineChoisi(null);
      setDossier(DOSSIER_VIDE);
      setMoyenPaiement('');
    } catch (err) {
      setErreur('Connexion impossible. Reessayez.');
    } finally {
      setEnvoi(false);
    }
  }

  /* ═══════════════ BLOCS D'AFFICHAGE ═══════════════ */

  function rendreCarte(ligne) {
    let etat = 'prise';
    if (ligne.verificationManuelle) etat = 'manuelle';
    else if (ligne.disponible) etat = 'libre';

    let libelle = 'Deja pris';
    if (etat === 'libre') libelle = 'Disponible';
    if (etat === 'manuelle') libelle = 'Verification manuelle';

    if (etat === 'prise') {
      return (
        <div className="rd-carte prise" key={ligne.domaine}>
          <div className="rd-infos">
            <div className="rd-nom">{ligne.domaine}</div>
            <span className="rd-badge prise">{libelle}</span>
          </div>
        </div>
      );
    }

    const prixPrincipal = ligne.surDemande
      ? 'Sur devis'
      : ligne.prix + ' $ / 1re annee';

    return (
      <div className={'rd-carte ' + etat} key={ligne.domaine}>
        <div className="rd-infos">
          <div className="rd-nom">{ligne.domaine}</div>
          <span className={'rd-badge ' + etat}>{libelle}</span>
        </div>
        <div className="rd-droite">
          <div className="rd-prix">
            <div className="rd-prix-principal">{prixPrincipal}</div>
            <div className="rd-prix-detail">
              {ligne.surDemande
                ? 'Tarif communique sous 24 h'
                : 'puis ' + ligne.prixRenouvellement + ' $ par an'}
            </div>
          </div>
          <button
            type="button"
            className="rd-choisir"
            onClick={function () {
              ouvrirDossier(ligne);
            }}
          >
            Demander
          </button>
        </div>
      </div>
    );
  }

  function rendreEtapeIdentite() {
    return (
      <div>
        <label className="rd-label">Vous etes</label>
        <div className="rd-choix-type">
          <button
            type="button"
            className={
              dossier.typeTitulaire === 'particulier' ? 'rd-type actif' : 'rd-type'
            }
            onClick={function () {
              majDossier('typeTitulaire', 'particulier');
            }}
          >
            Particulier
          </button>
          <button
            type="button"
            className={
              dossier.typeTitulaire === 'organisation' ? 'rd-type actif' : 'rd-type'
            }
            onClick={function () {
              majDossier('typeTitulaire', 'organisation');
            }}
          >
            Entreprise / ONG
          </button>
        </div>

        <div className={dossier.typeTitulaire === 'organisation' ? '' : 'rd-cache'}>
          <label className="rd-label">Nom de l'organisation</label>
          <input
            className="rd-saisie"
            value={dossier.organisation}
            onChange={function (e) {
              majDossier('organisation', e.target.value);
            }}
            placeholder="Eglise Bethel, ONG Espoir"
          />
        </div>

        <div className="rd-duo">
          <div>
            <label className="rd-label">Prenom</label>
            <input
              className="rd-saisie"
              value={dossier.prenom}
              onChange={function (e) {
                majDossier('prenom', e.target.value);
              }}
              placeholder="Isaac"
            />
          </div>
          <div>
            <label className="rd-label">Nom</label>
            <input
              className="rd-saisie"
              value={dossier.nom}
              onChange={function (e) {
                majDossier('nom', e.target.value);
              }}
              placeholder="Presmy"
            />
          </div>
        </div>

        <label className="rd-label">Telephone WhatsApp</label>
        <input
          className="rd-saisie"
          value={dossier.telephone}
          onChange={function (e) {
            majDossier('telephone', e.target.value);
          }}
          placeholder="+509 4032 5735"
        />
      </div>
    );
  }

  function rendreEtapeAdresse() {
    return (
      <div>
        <label className="rd-label">Adresse</label>
        <input
          className="rd-saisie"
          value={dossier.adresse}
          onChange={function (e) {
            majDossier('adresse', e.target.value);
          }}
          placeholder="Rue Georges Kernizan, 12"
        />

        <div className="rd-duo">
          <div>
            <label className="rd-label">Ville</label>
            <input
              className="rd-saisie"
              value={dossier.ville}
              onChange={function (e) {
                majDossier('ville', e.target.value);
              }}
              placeholder="Leogane"
            />
          </div>
          <div>
            <label className="rd-label">Code postal</label>
            <input
              className="rd-saisie"
              value={dossier.codePostal}
              onChange={function (e) {
                majDossier('codePostal', e.target.value);
              }}
              placeholder="HT6210"
            />
          </div>
        </div>

        <label className="rd-label">Departement</label>
        <select
          className="rd-selecteur"
          value={dossier.departement}
          onChange={function (e) {
            majDossier('departement', e.target.value);
          }}
        >
          {DEPARTEMENTS.map(function (d) {
            return (
              <option key={d} value={d}>
                {d}
              </option>
            );
          })}
        </select>

        <div className="rd-mention">
          Ces informations constituent le profil officiel du titulaire exige par
          l'ICANN pour tout enregistrement de domaine. Elles ne servent a rien
          d'autre.
        </div>
      </div>
    );
  }

  function rendreEtapePaiement() {
    const montantHtg = Math.round(
      domaineChoisi.prix * tauxChange
    ).toLocaleString('fr-FR');

    return (
      <div>
        <div className="rd-total">
          <div className="rd-total-usd">{domaineChoisi.prix} $ US</div>
          <div className="rd-total-htg">environ {montantHtg} HTG</div>
          <div className="rd-total-note">
            Taux PEFEP : 1 USD = {tauxChange} HTG. Renouvellement{' '}
            {domaineChoisi.prixRenouvellement} $ par an.
          </div>
        </div>

        <label className="rd-label">Moyen de paiement</label>
        <div className="rd-paiements">
          {PAIEMENTS.map(function (p) {
            return (
              <button
                type="button"
                key={p.cle}
                className={moyenPaiement === p.cle ? 'rd-paiement actif' : 'rd-paiement'}
                onClick={function () {
                  setMoyenPaiement(p.cle);
                  setErreur('');
                }}
              >
                <span className="rd-paiement-icone">{p.icone}</span>
                <span>
                  <span className="rd-paiement-titre">{p.cle}</span>
                  <span className="rd-paiement-detail">{p.detail}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="rd-mention">
          Le domaine est enregistre a votre nom en tant que titulaire. PEFEP Tech
          en assure la gestion technique et vous accompagne en cas de transfert.
        </div>
      </div>
    );
  }

  function rendreDossier() {
    if (!domaineChoisi) return null;

    let contenu = null;
    if (etape === 1) contenu = rendreEtapeIdentite();
    if (etape === 2) contenu = rendreEtapeAdresse();
    if (etape === 3) contenu = rendreEtapePaiement();

    const classe1 =
      etape === 1 ? 'rd-etape active' : etape > 1 ? 'rd-etape faite' : 'rd-etape';
    const classe2 =
      etape === 2 ? 'rd-etape active' : etape > 2 ? 'rd-etape faite' : 'rd-etape';
    const classe3 = etape === 3 ? 'rd-etape active' : 'rd-etape';

    return (
      <div className="rd-voile" onClick={fermerDossier}>
        <div
          className="rd-fenetre large"
          onClick={function (e) {
            e.stopPropagation();
          }}
        >
          <h3>Demander {domaineChoisi.domaine}</h3>

          <div className="rd-etapes">
            <div className={classe1}>1. Identite</div>
            <div className={classe2}>2. Adresse</div>
            <div className={classe3}>3. Paiement</div>
          </div>

          {contenu}

          <div className={erreur ? 'rd-message erreur' : 'rd-cache'}>{erreur}</div>

          <div className="rd-actions">
            <button
              type="button"
              className="rd-annuler"
              onClick={reculer}
              disabled={envoi}
            >
              {etape > 1 ? 'Retour' : 'Annuler'}
            </button>
            <button
              type="button"
              className="rd-bouton"
              onClick={etape < 3 ? validerEtape : envoyerDossier}
              disabled={envoi}
            >
              {etape < 3 ? 'Continuer' : envoi ? 'Envoi...' : 'Confirmer la demande'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function rendreConfirmation() {
    if (!confirmation) return null;

    function ouvrirWhatsApp() {
      window.open(confirmation.lienWhatsApp, '_blank', 'noopener,noreferrer');
    }

    return (
      <div className="rd-message info">
        <div className="rd-succes-titre">Demande enregistree</div>
        <div>{confirmation.message}</div>
        <div className="rd-reference">
          {confirmation.domaine} - Ref. {confirmation.id}
        </div>
        <button type="button" className="rd-whatsapp" onClick={ouvrirWhatsApp}>
          Confirmer sur WhatsApp
        </button>
        <div className="rd-express">
          Pour un traitement express, envoyez-nous le message pre-rempli.
        </div>
      </div>
    );
  }

  /* ═══════════════ VISITEUR NON CONNECTE ═══════════════ */

  if (!user) {
    return (
      <div className="rd-bloc">
        <div className="rd-connexion">
          <h3>Trouvez le nom de domaine de votre entreprise</h3>
          <p>
            Connectez-vous pour verifier la disponibilite d'un domaine et
            decouvrir nos tarifs en temps reel.
          </p>
          <button
            type="button"
            className="rd-bouton"
            onClick={function () {
              navigate('/connexion');
            }}
          >
            Se connecter pour rechercher
          </button>
        </div>
      </div>
    );
  }

  /* ═══════════════ AFFICHAGE PRINCIPAL ═══════════════ */

  const lignes = resultats && resultats.resultats ? resultats.resultats : [];
  const messageErreurGlobal = erreur && !domaineChoisi ? erreur : '';

  return (
    <div className="rd-bloc">
      <h2 className="rd-titre">{titre}</h2>
      <p className="rd-sous-titre">{sousTitre}</p>

      <form className="rd-formulaire" onSubmit={lancerRecherche}>
        <input
          className="rd-champ"
          type="text"
          value={saisie}
          onChange={function (e) {
            setSaisie(e.target.value);
          }}
          placeholder="monentreprise"
          maxLength={63}
          disabled={chargement}
          aria-label="Nom de domaine recherche"
        />
        <button className="rd-bouton" type="submit" disabled={chargement}>
          {chargement ? 'Recherche en cours...' : 'Rechercher'}
        </button>
      </form>

      <div className={messageErreurGlobal ? 'rd-message erreur' : 'rd-cache'}>
        {messageErreurGlobal}
      </div>

      {rendreConfirmation()}

      <div className="rd-liste">{lignes.map(rendreCarte)}</div>

      {rendreDossier()}
    </div>
  );
}

export default RechercheDomaine;